/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { YMMEContentBlockProps } from '@/features/ymme/components/YMMEContentBlock';
import { type ContentStackYmme } from '../../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { pageTypes } from '@/constants/page';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useEffect } from 'react';
import dynamic from 'next/dynamic';

const LazyYMMEContentBlock = dynamic(
  () => import('@/features/ymme/components/YMMEContentBlock').then((mod) => mod.YMMEContentBlock),
  { ssr: true },
);

const LazyAltYMMEContentBlock = dynamic(
  () => import('@/features/ymme/components/AltYMMEContentBlock').then((mod) => mod.default),
  { ssr: true },
);

type Props = {
  content: Omit<ContentStackYmme, 'theme' | 'title'>;
  redirectAfterVehicleChanged?: YMMEContentBlockProps['onUpdateVehicleSuccess'];
  removeBottomMargin?: boolean;
  removeMargins?: boolean;
  withPadding?: boolean;
  classes?: YMMEContentBlockProps['classes'];
  isProductFinderDisplayed?: boolean;
  nonShopPage?: boolean;
  pageType?: string;
  onHeightChange?: (height: string) => void;
};

export function CMSYmme({
  classes,
  content,
  redirectAfterVehicleChanged,
  removeBottomMargin,
  removeMargins,
  withPadding,
  isProductFinderDisplayed,
  pageType,
  onHeightChange,
  nonShopPage = false,
}: Props) {
  const { layout, text, sub_text } = content;
  const { alternativeYMMEContentBlock: altYMMEContentBlock } = useAcesFlags();

  const layoutType = layout?.toLowerCase() as Lowercase<NonNullable<ContentStackYmme['layout']>>;

  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const { data: headerData } = useHeaderData();
  const router = useLegacyRouter();

  // Check if we're on the search result page
  const isSearchResultPage = router.pathname === '/searchresult';

  // On the search page YMME must always show (AC #6: force YMME when product finder data exists).
  // For other search-page renders from non-CMS paths, keep the legacy mobile+vehicle suppression.
  const shouldRenderComponent =
    isMobile &&
    !!headerData?.vehicleMap?.vehicleName &&
    isSearchResultPage &&
    pageType !== 'search';

  useEffect(() => {
    if (onHeightChange) {
      onHeightChange(isMobile ? '100%' : '115px');
    }
  }, [isMobile, onHeightChange]);

  return (
    <>
      {!shouldRenderComponent && (
        <>
          {altYMMEContentBlock ? (
            <LazyAltYMMEContentBlock
              nonShopPage={nonShopPage}
              pageType={pageType}
              removeMargins={removeMargins}
            />
          ) : (
            <LazyYMMEContentBlock
              ymmeTitle={text}
              ymmeDesc={sub_text}
              layoutType={layoutType}
              onUpdateVehicleSuccess={redirectAfterVehicleChanged}
              classes={classes}
              removeBottomMargin={removeBottomMargin}
              removeMargins={removeMargins}
              withPadding={withPadding}
              isProductFinderDisplayed={isProductFinderDisplayed}
              pageType={pageTypes.shelf}
            />
          )}
        </>
      )}
    </>
  );
}
